// SendTab — 发送 Tab
// 复刻 LocalSend send_tab.dart：文件 BigButton 网格 + 选中文件缩略图条 + 附近设备 grid

import { useRef, useState } from 'react';
import { BigButton } from '../common/BigButton';
import { DeviceCard } from '../devices/DeviceCard';
import { FileTransferProgress } from '../files/FileTransferProgress';
import { useDeviceStore } from '../../store/deviceStore';
import { useSessionStore } from '../../store/sessionStore';
import { useToastStore } from '../common/Toast';
import type { UseWebRTCPeerResult } from '../../hooks/useWebRTCPeer';
import type { SignalingClient } from '../../lib/signaling/client';
import type { ClientInfo } from '@kuaichuan/shared';

interface SendTabProps {
  webrtc: UseWebRTCPeerResult;
  signaling: SignalingClient | null;
  onJoinRoom: () => void;
}

export function SendTab({ webrtc, signaling, onJoinRoom }: SendTabProps) {
  const { peers } = useDeviceStore();
  const sessionStore = useSessionStore();
  const toast = useToastStore((s) => s.show);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const handlePickFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setSelectedFiles(Array.from(files));
  };

  const handleSendTo = async (peer: ClientInfo) => {
    if (!signaling) {
      toast('未连接到房间', 'error');
      return;
    }
    if (selectedFiles.length === 0) {
      toast('请先选择文件', 'warning');
      return;
    }
    await webrtc.sendTo(signaling, peer.id, peer.alias, selectedFiles);
  };

  const transferring = sessionStore.status === 'transferring' || sessionStore.status === 'connected';

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto pb-24 md:pb-8">
      {/* 文件选择区 */}
      <section className="mb-6">
        <h3 className="text-sm font-medium text-muted mb-3 px-1">选择发送内容</h3>
        <div className="flex flex-wrap gap-3">
          <BigButton icon="📄" label="文件" onClick={() => fileInputRef.current?.click()} />
          <BigButton
            icon="🖼️"
            label="图片"
            onClick={() => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = 'image/*';
              input.multiple = true;
              input.onchange = () => handlePickFiles(input.files);
              input.click();
            }}
          />
          <BigButton
            icon="🎬"
            label="视频"
            onClick={() => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = 'video/*';
              input.multiple = true;
              input.onchange = () => handlePickFiles(input.files);
              input.click();
            }}
          />
          <BigButton
            icon="📝"
            label="文本"
            onClick={() => {
              const text = window.prompt('输入要发送的文本:');
              if (text) {
                const blob = new Blob([text], { type: 'text/plain' });
                const file = new File([blob], '文本.txt', { type: 'text/plain' });
                setSelectedFiles([file]);
              }
            }}
          />
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => handlePickFiles(e.target.files)}
          />
        </div>

        {/* 已选文件预览 */}
        {selectedFiles.length > 0 && (
          <div className="mt-4 card p-4 animate-slide-up">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium">
                已选 {selectedFiles.length} 个文件
                <span className="text-muted ml-2">
                  ({formatBytes(selectedFiles.reduce((s, f) => s + f.size, 0))})
                </span>
              </span>
              <button
                type="button"
                onClick={() => setSelectedFiles([])}
                className="text-xs text-muted hover:text-danger"
              >
                清除
              </button>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {selectedFiles.map((f, i) => (
                <div
                  key={i}
                  className="shrink-0 w-16 h-16 rounded-md bg-bg border border-border flex items-center justify-center text-2xl"
                  title={f.name}
                >
                  {fileEmoji(f)}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* 附近设备 */}
      <section>
        <h3 className="text-sm font-medium text-muted mb-3 px-1">附近设备</h3>
        {peers.length === 0 ? (
          <div className="card p-10 text-center">
            <div className="text-5xl mb-3 opacity-50">📡</div>
            <p className="text-muted">暂无设备连接</p>
            <p className="text-xs text-muted mt-1">创建房间或输入配对码加入</p>
            <button type="button" onClick={onJoinRoom} className="btn-outline mt-4">
              🔢 输入配对码 / 扫码加入
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {peers.map((peer) => (
              <DeviceCard
                key={peer.id}
                peer={peer}
                onSend={() => handleSendTo(peer)}
                disabled={transferring}
              />
            ))}
          </div>
        )}
      </section>

      {/* 文件传输进度（传输中显示） */}
      {sessionStore.status !== 'idle' && sessionStore.files.length > 0 && (
        <FileTransferProgress
          files={sessionStore.files}
          status={sessionStore.status}
          role={sessionStore.role}
          remoteAlias={sessionStore.remotePeerAlias}
          onCancel={webrtc.cancelTransfer}
          onAccept={(ids) => webrtc.acceptSelected(ids)}
          onDecline={webrtc.declineIncoming}
          pendingIncoming={webrtc.pendingIncomingFiles}
        />
      )}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fileEmoji(file: File): string {
  const t = file.type;
  if (t.startsWith('image/')) return '🖼️';
  if (t.startsWith('video/')) return '🎬';
  if (t.startsWith('audio/')) return '🎵';
  if (t.startsWith('text/')) return '📝';
  if (t.includes('pdf')) return '📕';
  if (t.includes('zip') || t.includes('compressed')) return '🗜️';
  return '📄';
}
